import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Nav */}
      <nav className="bg-slate-900 border-b border-yellow-500/20 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="cursor-pointer inline-block">
          <img
            src="https://static.readdy.ai/image/870e0649015e5e428345464cd8040611/e2821367afaf48576bf0fa4cd75aa99b.png"
            alt="Guardian Veterans LLC"
            className="h-[48px] w-auto object-contain"
          />
        </button>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          Back to Home
        </button>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="w-12 h-12 flex items-center justify-center bg-yellow-500/20 rounded-full mx-auto mb-4">
            <i className="ri-shield-check-line text-yellow-400 text-2xl"></i>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">Last updated: March 22, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-14 text-slate-700 leading-relaxed space-y-10">

        <section>
          <p className="text-base">
            Guardian Veterans LLC (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is a veteran-owned Non-Emergency Medical
            Transportation (NEMT) company based in Waddell, Arizona. We are committed to protecting the
            privacy of our clients, website visitors, and anyone who interacts with us. This Privacy Policy
            explains what information we collect, how we use it, and your rights regarding that information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">1. Information We Collect</h2>
          <p className="mb-3">We may collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li><strong>Contact Information:</strong> Name, phone number, email address, and mailing address provided when you fill out a form, request a booking, or contact us.</li>
            <li><strong>Medical &amp; Transportation Details:</strong> Information necessary to arrange your ride, such as appointment location, mobility needs, wheelchair requirements, or facility information.</li>
            <li><strong>Billing Information:</strong> Payment details related to private-pay arrangements, such as agreed-upon rates and scheduling confirmations.</li>
            <li><strong>Website Usage Data:</strong> Browser type, pages visited, time spent on the site, and referring URLs collected automatically through standard web analytics.</li>
            <li><strong>Communications:</strong> Messages, inquiries, and correspondence you send us via email or contact forms.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">2. How We Use Your Information</h2>
          <p className="mb-3">We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Schedule and coordinate Non-Emergency Medical Transportation rides</li>
            <li>Respond to inquiries, requests, and messages</li>
            <li>Communicate appointment confirmations, updates, or cancellations</li>
            <li>Process and manage partnerships with medical facilities</li>
            <li>Improve our website and service quality</li>
            <li>Comply with applicable laws, regulations, and licensing requirements</li>
            <li>Send you service-related updates (we do not send unsolicited marketing emails)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">3. How We Share Your Information</h2>
          <p className="mb-3">
            We do <strong>not</strong> sell, rent, or trade your personal information. We may share information only in the following limited circumstances:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li><strong>Service Fulfillment:</strong> With drivers or partner transportation providers strictly to coordinate your ride.</li>
            <li><strong>Medical Facilities:</strong> With the facility or provider you are traveling to or from, as needed to confirm your appointment.</li>
            <li><strong>Legal Compliance:</strong> If required by law, court order, or government authority.</li>
            <li><strong>Business Operations:</strong> With trusted service providers (such as scheduling software or email tools) who are contractually obligated to protect your data.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">4. Protected Health Information (PHI)</h2>
          <p className="text-sm">
            As a transportation provider serving individuals with medical needs, some information we collect may be considered Protected Health Information (PHI) under HIPAA. We take appropriate steps to protect PHI in accordance with applicable federal and state requirements. We only use or disclose health-related information as necessary to provide transportation services or as required by law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">5. Data Security</h2>
          <p className="text-sm">
            We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the Internet or electronic storage is 100% secure. We encourage you to contact us via phone at{' '}
            <a href="tel:+16022956626" className="text-yellow-600 hover:underline">(602) 295-6626</a> for highly sensitive matters.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">6. Cookies &amp; Analytics</h2>
          <p className="text-sm">
            Our website may use cookies or similar tracking technologies to understand how visitors use our site and to improve the user experience. This information is collected in aggregate and is not used to personally identify you. You can disable cookies in your browser settings, though some features of the site may not function as intended.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">7. Third-Party Links</h2>
          <p className="text-sm">
            Our website may contain links to third-party websites (such as Google Maps or social media platforms). We are not responsible for the privacy practices of those websites and encourage you to review their privacy policies independently.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">8. Children&apos;s Privacy</h2>
          <p className="text-sm">
            Our services are not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us so we can delete it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">9. Your Rights</h2>
          <p className="mb-3 text-sm">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Request access to the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information (subject to legal retention requirements)</li>
            <li>Opt out of non-essential communications at any time</li>
          </ul>
          <p className="mt-3 text-sm">
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:info@gvnemt.com" className="text-yellow-600 hover:underline">info@gvnemt.com</a>{' '}
            or call <a href="tel:+16022956626" className="text-yellow-600 hover:underline">(602) 295-6626</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">10. Changes to This Policy</h2>
          <p className="text-sm">
            We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last updated&quot; date at the top of this page. We encourage you to review this page periodically to stay informed about how we protect your information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-3">11. Contact Us</h2>
          <p className="text-sm mb-4">
            If you have any questions or concerns about this Privacy Policy, please reach out:
          </p>
          <div className="bg-slate-50 rounded-lg p-5 text-sm space-y-2 border border-slate-200">
            <p className="font-semibold text-slate-900">Guardian Veterans LLC</p>
            <p>Waddell, AZ 85355</p>
            <p>
              Phone:{' '}
              <a href="tel:+16022956626" className="text-yellow-600 hover:underline">(602) 295-6626</a>
            </p>
            <p>
              Email:{' '}
              <a href="mailto:info@gvnemt.com" className="text-yellow-600 hover:underline">info@gvnemt.com</a>
            </p>
          </div>
        </section>
      </div>

      {/* Footer Strip */}
      <div className="bg-slate-900 text-slate-400 text-sm text-center py-6 px-4 border-t border-yellow-500/20">
        <span>© 2026 Guardian Veterans LLC · </span>
        <button onClick={() => navigate('/privacy-policy')} className="text-yellow-400 hover:underline cursor-pointer whitespace-nowrap">Privacy Policy</button>
        <span> · </span>
        <button onClick={() => navigate('/terms-of-service')} className="text-yellow-400 hover:underline cursor-pointer whitespace-nowrap">Terms of Service</button>
      </div>
    </div>
  );
}
